import { Flex, Input } from 'antd';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CreateGroup from '../../components/CreateGroup';
import PlaceholderContent from '../../components/PlaceholderContent';
import { getGroups } from './../../redux/actions/groups';
import GroupList from '../../components/GroupList';
import { useParams } from 'react-router-dom';
import GroupDetails from './Details';
import AddMembers from '../../components/AddMembers';
import { decodeId } from '../../utils/secureId';

const Groups = () => {
    const { groupId: group_id } = useParams();
    const groupId = group_id && decodeId(group_id);
    const [filter, setFilter] = useState({ search: false });
    const [groupData, setGroupData] = useState(null);
    const groups = useSelector((state) => state.groupSlice.groups);
    const [addMembers, setAddMembers] = useState(null);
    const [filterData, setFilterData] = useState({
        search: null,
    });

    const debouncedSearch = useMemo(
        () =>
            _.debounce((value) => {
                getGroups({ search: value });
            }, 500),
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilterData((prev) => ({ ...prev, search: value }));
        debouncedSearch(value);
    };

    useEffect(() => {
        getGroups();
    }, []);

    return (
        <div className={clsx('Chat Group', { 'Active_Group': groupId })}>
            <div className="Chat__ChatList">
                <div className="Chat__Header List_Header">
                    <Flex flex={1} justify="space-between" align="center" gap={10} className="top-bar">
                        {/* Left Title / Back Icon */}
                        <AnimatePresence mode="wait">
                            {_.some(_.pick(filter, Object.keys(filter))) ? (
                                <motion.div
                                    key="back-icon"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <ArrowLeft
                                        size={22}
                                        cursor="pointer"
                                        onClick={() => {
                                            setFilter({ search: false });
                                        }}
                                    />
                                </motion.div>
                            ) : (
                                <motion.span
                                    key="title"
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    Groups
                                </motion.span>
                            )}
                        </AnimatePresence>

                        {/* Right Actions */}
                        <AnimatePresence mode="wait">
                            {!filter.search && (
                                <motion.div
                                    key="search-icon"
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.2 }}
                                    className="icon-container"
                                >
                                    <Plus
                                        size={22}
                                        color="rgb(156,163,175)"
                                        cursor="pointer"
                                        onClick={() => setGroupData({ open: true, data: null })}
                                    />

                                    <Search
                                        size={22}
                                        color="rgb(156,163,175)"
                                        cursor="pointer"
                                        onClick={() => setFilter({ search: true })}
                                    />
                                </motion.div>
                            )}

                            {filter.search && (
                                <motion.div
                                    key="search-bar"
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.2 }}
                                    style={{ flex: 1 }}
                                >
                                    <Input
                                        placeholder="Search..."
                                        allowClear
                                        prefix={<Search size={18} color="rgb(156,163,175)" />}
                                        autoFocus
                                        value={filterData?.search}
                                        onChange={handleSearch}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Flex>
                </div>

                <GroupList groups={groups} />
            </div>

            <div className="Chat__Window">
                {groupId ? (
                    <GroupDetails />
                ) : (
                    <div className="Chat__Home">
                        <div className="chat-content">
                            <PlaceholderContent />
                        </div>
                    </div>
                )}
            </div>

            {groupData && (
                <CreateGroup
                    open={groupData?.open}
                    onCancel={() => setGroupData(null)}
                    data={groupData?.data}
                    onNewGroupCreated={(newGroup) => {
                        setAddMembers(newGroup);
                    }}
                />
            )}

            {addMembers && (
                <AddMembers
                    open={!!addMembers}
                    onCancel={() => setAddMembers(null)}
                    group={addMembers}
                />
            )}
        </div>
    );
};

export default Groups;
